import { createTranscriptView } from "../../../../packages/app-ui/js/transcriptView.js";
import { createTranscriptStats } from "../../../../packages/app-ui/js/transcriptStats.js";
import { createTranscriptSearch } from "../../../../packages/app-ui/js/transcriptSearch.js";
import { extractTranscriptText } from "../utils/transcriptUtils.js";

const TRANSCRIPT_CACHE_KEY = "sift:transcript-cache";
const TRANSCRIPT_CACHE_VERSION = 1;
const MAX_CACHED_TRANSCRIPTS = 20;
const PREFETCH_COUNT = 3;

export function createTranscriptUi({
  document,
  window,
  jobsStore,
  backendAdapter,
  libraryRefs,
}) {
  // in-memory LRU cache, hydrated from localStorage on init
  const transcriptCache = new Map();
  const fallbackTranscript = "";

  // load persisted cache from localStorage on init
  function hydrateFromStorage() {
    try {
      const raw = localStorage.getItem(TRANSCRIPT_CACHE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (stored.version !== TRANSCRIPT_CACHE_VERSION) return;
      // restore in order (oldest first, so newest ends up at end)
      (stored.entries || []).forEach(([key, value]) => {
        transcriptCache.set(key, value);
      });
      console.log(
        `[sift-perf] transcript-cache: hydrated ${transcriptCache.size} from localStorage`,
      );
    } catch {
      // corrupt cache, ignore
    }
  }

  // persist cache to localStorage
  function persistToStorage() {
    try {
      const entries = Array.from(transcriptCache.entries());
      const payload = {
        version: TRANSCRIPT_CACHE_VERSION,
        entries,
        cachedAt: Date.now(),
      };
      localStorage.setItem(TRANSCRIPT_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full or unavailable
    }
  }

  // hydrate on module init
  hydrateFromStorage();

  // simple LRU: delete oldest entry when at capacity
  function cacheSet(key, value) {
    if (transcriptCache.has(key)) {
      transcriptCache.delete(key);
    } else if (transcriptCache.size >= MAX_CACHED_TRANSCRIPTS) {
      const oldestKey = transcriptCache.keys().next().value;
      transcriptCache.delete(oldestKey);
    }
    transcriptCache.set(key, value);
    // persist after every new entry
    persistToStorage();
  }

  function cacheGet(key) {
    if (!transcriptCache.has(key)) return undefined;
    const value = transcriptCache.get(key);
    transcriptCache.delete(key);
    transcriptCache.set(key, value);
    return value;
  }

  async function loadTranscriptContent(name) {
    const file = jobsStore.findByName(name);
    if (!file || !file.jobId) {
      return "";
    }
    const cacheKey = file.jobId;
    const cacheStart = performance.now();
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) {
      console.log(
        `[sift-perf] transcript-load: cache-hit ${(performance.now() - cacheStart).toFixed(1)}ms`,
      );
      return cached;
    }
    const start = performance.now();
    try {
      const payload = await backendAdapter.getTranscript(file.jobId);
      console.log(
        `[sift-perf] transcript-load: fetch ${(performance.now() - start).toFixed(1)}ms`,
      );
      const transcriptText = extractTranscriptText(payload?.transcript);
      cacheSet(cacheKey, transcriptText);
      return transcriptText;
    } catch (error) {
      const status =
        error && typeof error === "object" ? error.status || null : null;
      if (status === 409) {
        // transcript not ready yet; surface empty content and let polling/refresh pick it up later
        console.warn("transcript_not_ready_yet", {
          jobId: file.jobId,
          name: file.name,
        });
        return fallbackTranscript;
      }
      throw error;
    }
  }

  const transcriptView = createTranscriptView({
    document,
    window,
    dummyFiles: jobsStore.files,
    transcriptCache,
    fallbackTranscript,
    demoRecorderSecondSegment: "",
    getTranscriptBaseName: (value) => value || "",
    getDisplayName: (name) => {
      const file = jobsStore.findByName(name);
      return file?.displayName || name;
    },
    getTranscriptCacheKey: (name) => {
      const file = jobsStore.findByName(name);
      return file?.jobId || name;
    },
    loadTranscriptContent,
    formatFullDate: (date) => {
      if (!(date instanceof Date)) {
        return "n/a";
      }
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    updateDemoButtons: () => {},
    applyPendingInDocSearchPreset: (filename) =>
      libraryRefs?.applyPendingInDocSearchPreset?.(filename),
    setFilter: (value) => libraryRefs?.setFilter?.(value),
    showLibrary: () => libraryRefs?.showLibrary?.(),
    showVendingMachine: () => {},
    renderFileList: (...args) => libraryRefs?.renderFileList?.(...args),
    refreshFileRow: (...args) => libraryRefs?.refreshFileRow?.(...args),
    forceCompleteFile: () => {},
    cancelFile: (...args) => libraryRefs?.cancelFile?.(...args),
  });

  const transcriptElements = transcriptView.getTranscriptElements();
  const stats = createTranscriptStats({
    document,
    window,
    transcriptElements,
    numberFormatter: new Intl.NumberFormat("en-US"),
  });
  if (typeof transcriptView.setStatsHandlers === "function") {
    transcriptView.setStatsHandlers({
      updateTranscriptStats: stats.updateTranscriptStats,
      updateSelectionStats: stats.updateSelectionStats,
    });
  }

  const transcriptSearch = createTranscriptSearch({
    document,
    window,
    transcriptElements,
    getCurrentTranscriptContent: () =>
      transcriptView.getCurrentTranscriptContent(),
    setCurrentTranscriptContent: (value) =>
      transcriptView.setCurrentTranscriptContent(value),
    getCurrentTranscriptFilename: () =>
      transcriptView.getCurrentTranscriptFilename(),
    getTranscriptScrollbar: () => transcriptView.getTranscriptScrollbar(),
    getSearchMode: () => libraryRefs?.getSearchMode?.() || "files",
    setSearchMode: (mode) => libraryRefs?.setSearchMode?.(mode),
    getSearchTerm: () => libraryRefs?.getSearchTerm?.() || "",
    contentHits: libraryRefs?.getContentHits?.() || new Map(),
    contentSearchMinLength: 2,
    filterFiles: (term) => libraryRefs?.filterFiles?.(term),
    viewTranscript: (name) => transcriptView.viewTranscript(name),
    maybeQueueInDocSearchPreset: (...args) =>
      libraryRefs?.maybeQueueInDocSearchPreset?.(...args),
    libraryViewElement: libraryRefs?.getLibraryViewElement?.(),
  });

  // expose search hotkeys globally for keyboard shortcuts
  try {
    window.SearchHotkeys = transcriptSearch.hotkeys;
  } catch (_) {}

  // prefetch first N completed transcripts (most recent first)
  // fire-and-forget, errors are silently ignored
  function prefetchRecent() {
    const completed = jobsStore.files
      .filter((f) => f.status === "complete" && f.jobId)
      .slice(0, PREFETCH_COUNT);
    if (completed.length === 0) return;
    console.log(
      `[sift-perf] prefetching ${completed.length} recent transcripts`,
    );
    completed.forEach((file) => {
      // skip if already cached
      if (transcriptCache.has(file.jobId)) return;
      // fire and forget
      loadTranscriptContent(file.name).catch(() => {});
    });
  }

  return {
    viewTranscript: (name) => transcriptView.viewTranscript(name),
    hideTranscriptView: () => transcriptView.hideTranscriptView(),
    loadTranscriptContent,
    prefetchRecent,
    getTranscriptElements: () => transcriptElements,
    getTranscriptSearchHotkeys: () => transcriptSearch.hotkeys,
    getTranscriptView: () => transcriptView,
    updateTranscriptStats: stats.updateTranscriptStats,
  };
}
